import { useMemo } from 'react'
import { AccessType } from './types'
import { GenericFetchHookParamsType } from '@signal/core/contextFetch'
import { useSWRWithCache } from '@signal/core/swr'
import getAPIDomain from '@signal/utils/env/apiDomain'
import { UserAccountWithPermissionsType } from '../user/types'

export interface UseAccountPermissionsType extends GenericFetchHookParamsType {
  accountId?: string | null
}

export function getPermissionsForAccountPath(
  accountId: string | null | undefined
): string {
  if (!accountId) return ''
  return `${getAPIDomain('/menu/service')}/v1/accounts/${accountId}`
}

export function getTenantPermissionsPath(): string {
  return `${getAPIDomain('/menu/service')}/v1/tenants/permissions`
}

/**
 * this is a wrapper around useSWRWithCache that can be used to fetch account permissions
 * @return {PermissionsType | undefined} - returns undefined unless the data has been fetched,
 *   in which case the permission data is returned
 */
export function useAccountPermissions({
  accountId,
  ...rest
}: UseAccountPermissionsType): Array<AccessType> | undefined {
  const url = useMemo(() => getPermissionsForAccountPath(accountId), [
    accountId
  ])

  // Skip fetch if no accountId - return undefined to indicate loading/not ready
  const ret = useSWRWithCache({
    cacheBust: true,
    context: 'useAccountPermissions',
    url, // SWR will skip fetch if url is empty string
    transformFunc: (response: unknown): Array<AccessType> => {
      const access = (response as UserAccountWithPermissionsType | undefined)
        ?.access
      return Array.isArray(access) ? access : []
    },
    ...rest
  })
  return ret as Array<AccessType> | undefined
}

/**
 * this is a wrapper around useSWRWithCache that can be used to fetch account permissions
 * @return {PermissionsType | undefined} - returns undefined unless the data has been fetched,
 *   in which case the permission data is returned
 */
export function useTenantPermissions(
  props: GenericFetchHookParamsType
): Array<AccessType> | undefined {
  const url = useMemo(() => getTenantPermissionsPath(), [])
  const ret = useSWRWithCache({
    cacheBust: true,
    context: 'useTenantPermissions',
    url,
    ...props
  })
  return ret as Array<AccessType> | undefined
}
